import React, { useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchEmailConfig, saveEmailConfig, testEmailConfig } from '../../services/api.js';
import { Btn, Field, Input, Select, Toast, Spinner } from '../../components/ui.jsx';

/* ── SMTP presets ──────────────────────────────────────────────────────────── */
const SMTP_PRESETS = [
  { label:'Gmail',            host:'smtp.gmail.com',       port:587, enc:'tls', note:'Use App Password, not account password' },
  { label:'Outlook / Office', host:'smtp-mail.outlook.com',port:587, enc:'tls', note:'Works with Office 365 / Outlook.com' },
  { label:'Yahoo Mail',       host:'smtp.mail.yahoo.com',  port:587, enc:'tls', note:'Enable "Allow apps that use less secure sign in"' },
  { label:'Zoho Mail',        host:'smtp.zoho.com',        port:587, enc:'tls', note:'Ideal for business domains on Zoho' },
  { label:'SendGrid',         host:'smtp.sendgrid.net',    port:587, enc:'tls', note:'Transactional email — use API key as password' },
  { label:'Mailgun',          host:'smtp.mailgun.org',     port:587, enc:'tls', note:'High-deliverability bulk & transactional' },
  { label:'Amazon SES',       host:'email-smtp.us-east-1.amazonaws.com', port:587, enc:'tls', note:'AWS region varies — check SES console' },
  { label:'Custom SMTP',      host:'',                     port:587, enc:'tls', note:'Enter your mail server details manually' },
];

/* ── International email templates ────────────────────────────────────────── */
const buildTemplates = (hotelName = '{{hotelName}}', city = '{{city}}', phone = '{{phone}}', email = '{{email}}') => ({
  confirmation: {
    label: 'Booking Confirmation',
    icon: '📋',
    subject: `Your booking is confirmed — ${hotelName}`,
    body: `Dear {{guestName}},

Thank you for choosing ${hotelName}. We are delighted to confirm your reservation.

━━ RESERVATION DETAILS ━━
Confirmation No.: {{confirmationNumber}}
Room Type:        {{roomType}}
Check-in:         {{checkInDate}} from {{checkInTime}}
Check-out:        {{checkOutDate}} by {{checkOutTime}}
Guests:           {{adults}} adult(s){{#children}}, {{children}} child(ren){{/children}}
Meal Plan:        {{mealPlan}}

━━ RATE SUMMARY ━━
Room Rate:        {{currency}} {{ratePerNight}} / night
Total ({{nights}} night{{#nights>1}}s{{/nights>1}}): {{currency}} {{totalAmount}}

━━ HOTEL CONTACT ━━
${hotelName}
${city ? city + '\n' : ''}${phone ? 'Tel: ' + phone + '\n' : ''}${email ? 'Email: ' + email : ''}

If you have any special requests or questions, please do not hesitate to contact us.

We look forward to welcoming you.

Warm regards,
The Reservations Team
${hotelName}`,
  },

  checkinReminder: {
    label: 'Check-in Reminder',
    icon: '🛎️',
    subject: `Your stay at ${hotelName} is tomorrow — check-in details inside`,
    body: `Dear {{guestName}},

We are excited to welcome you to ${hotelName} tomorrow!

━━ YOUR ARRIVAL DETAILS ━━
Check-in Date:  {{checkInDate}}
Check-in Time:  From {{checkInTime}}
Confirmation:   {{confirmationNumber}}
Room Type:      {{roomType}}

━━ GETTING HERE ━━
{{hotelAddress}}
{{#directions}}{{directions}}{{/directions}}

━━ WHAT TO BRING ━━
• Government-issued photo ID / passport
• Credit card used for booking (if applicable)
• Your confirmation number: {{confirmationNumber}}

Early check-in and late check-out are subject to availability. Please contact us if you need special arrangements.

We look forward to seeing you!

Best regards,
${hotelName}
${phone ? 'Tel: ' + phone : ''}`,
  },

  checkout: {
    label: 'Check-out Receipt',
    icon: '🧾',
    subject: `Your stay receipt — ${hotelName} · {{confirmationNumber}}`,
    body: `Dear {{guestName}},

Thank you for staying with us at ${hotelName}. We hope you had a wonderful experience.

━━ STAY SUMMARY ━━
Confirmation:  {{confirmationNumber}}
Room:          {{roomNumber}} — {{roomType}}
Check-in:      {{checkInDate}}
Check-out:     {{checkOutDate}}
Nights:        {{nights}}

━━ CHARGES ━━
{{chargeLines}}
─────────────────────────────
TOTAL PAID:    {{currency}} {{totalPaid}}

━━ PAYMENT METHOD ━━
{{paymentSummary}}

This email serves as your official receipt for tax purposes.

We would love to welcome you back. Book directly at {{bookingUrl}} for the best rates.

Thank you and safe travels,
${hotelName}
${phone ? 'Tel: ' + phone : ''}
${email ? email : ''}`,
  },

  cancellation: {
    label: 'Cancellation Notice',
    icon: '❌',
    subject: `Reservation cancelled — ${hotelName} · {{confirmationNumber}}`,
    body: `Dear {{guestName}},

We confirm that your reservation has been cancelled as requested.

━━ CANCELLED RESERVATION ━━
Confirmation:    {{confirmationNumber}}
Original dates:  {{checkInDate}} — {{checkOutDate}}
Room Type:       {{roomType}}
Cancelled on:    {{cancellationDate}}

━━ REFUND POLICY ━━
{{cancellationPolicy}}

{{#refundAmount}}
A refund of {{currency}} {{refundAmount}} will be processed to your original payment method within 5–10 business days.
{{/refundAmount}}

We are sorry to see you go. Should you wish to re-book, please visit us at {{bookingUrl}} or call {{phone}}.

We hope to welcome you on a future occasion.

Kind regards,
${hotelName}
${phone ? 'Tel: ' + phone : ''}`,
  },

  payment: {
    label: 'Payment Receipt',
    icon: '💳',
    subject: `Payment received — ${hotelName} · {{confirmationNumber}}`,
    body: `Dear {{guestName}},

We confirm receipt of your payment. Details are below.

━━ PAYMENT DETAILS ━━
Transaction Date:  {{paymentDate}}
Amount Paid:       {{currency}} {{amount}}
Payment Method:    {{method}}
Reference:         {{reference}}

━━ RESERVATION ━━
Confirmation No.:  {{confirmationNumber}}
Room:              {{roomNumber}} — {{roomType}}
Period:            {{checkInDate}} → {{checkOutDate}}

Remaining Balance: {{currency}} {{balance}}

Please retain this email for your records.

Thank you,
${hotelName}
${phone ? 'Tel: ' + phone : ''}
${email ? email : ''}`,
  },

  staffWelcome: {
    label: 'Staff Welcome',
    icon: '👤',
    subject: `Welcome to ${hotelName} — your account is ready`,
    body: `Dear {{staffName}},

Welcome to the team at ${hotelName}!

Your staff account has been created. Below are your login credentials.

━━ YOUR LOGIN DETAILS ━━
System URL:  {{systemUrl}}
Username:    {{username}}
Password:    {{tempPassword}}

Please log in and change your password immediately.

Your role is: {{role}}

If you have any questions or need assistance, please contact your system administrator or manager.

Welcome aboard,
${hotelName} Management
${phone ? 'Tel: ' + phone : ''}`,
  },
});

/* ── Sidebar nav item ──────────────────────────────────────────────────────── */
function NavItem({ icon, label, desc, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:11, padding:'10px 13px', borderRadius:11,
      cursor:'pointer', border:`1px solid ${active ? T.teal+'50' : 'transparent'}`,
      background: active ? `${T.teal}10` : 'transparent', marginBottom:3,
      transition:'all .18s',
    }}>
      <span style={{ fontSize:18 }}>{icon}</span>
      <div>
        <p style={{ fontSize:12, fontWeight:700, color: active ? T.teal : T.navy }}>{label}</p>
        <p style={{ fontSize:10, color:T.muted, marginTop:1 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ── Variable chip ─────────────────────────────────────────────────────────── */
function VarChip({ name }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(`{{${name}}}`); setCopied(true); setTimeout(()=>setCopied(false),1500); }}
      style={{
        fontSize:9, fontFamily:'monospace', fontWeight:700, padding:'3px 8px', borderRadius:6,
        border:`1px solid ${T.teal}40`, background:`${T.teal}10`, color: copied ? T.success : T.teal,
        cursor:'pointer', transition:'all .2s', whiteSpace:'nowrap',
      }}>
      {copied ? '✓' : `{{${name}}}`}
    </button>
  );
}

export default function EmailConfig({ property }) {
  const [cfg,      setCfg]     = useState(null);
  const [loading,  setLoad]    = useState(true);
  const [saving,   setSaving]  = useState(false);
  const [testing,  setTesting] = useState(false);
  const [testEmail,setTestEmail]=useState('');
  const [toast,    setToast]   = useState('');
  const [tab,      setTab]     = useState('smtp');
  const [tplKey,   setTplKey]  = useState('confirmation');
  const [dirty,    setDirty]   = useState(false);
  const [preview,  setPreview] = useState(false);

  const pid = property?.id;
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const TEMPLATES = buildTemplates(
    property?.name || 'Hotel',
    property?.city,
    property?.phone,
    property?.email,
  );

  function defaultCfg() {
    const T0 = TEMPLATES;
    return {
      senderName: property?.name ? `${property.name} Reservations` : '',
      senderEmail:'', replyTo:'',
      smtpHost:'', smtpPort:587, smtpUsername:'', smtpPassword:'',
      smtpEncryption:'tls', smtpEnabled:false,
      templates: {
        confirmation:    { subject: T0.confirmation.subject,    body: T0.confirmation.body,    enabled:true },
        checkinReminder: { subject: T0.checkinReminder.subject, body: T0.checkinReminder.body, enabled:true },
        checkout:        { subject: T0.checkout.subject,        body: T0.checkout.body,        enabled:true },
        cancellation:    { subject: T0.cancellation.subject,    body: T0.cancellation.body,    enabled:true },
        payment:         { subject: T0.payment.subject,         body: T0.payment.body,         enabled:true },
        staffWelcome:    { subject: T0.staffWelcome.subject,    body: T0.staffWelcome.body,    enabled:true },
      },
    };
  }

  useEffect(() => {
    if (!pid) return;
    setLoad(true);
    fetchEmailConfig(pid)
      .then(data => {
        if (data) {
          // Merge saved templates with defaults so new template keys always exist
          const defaults = defaultCfg();
          const merged = {
            ...defaults,
            ...data,
            templates: { ...defaults.templates, ...(data.templates || {}) },
          };
          setCfg(merged);
        } else {
          setCfg(defaultCfg());
        }
      })
      .catch(() => setCfg(defaultCfg()))
      .finally(() => setLoad(false));
  }, [pid]);

  const set = (k, v) => { setCfg(p => ({...p, [k]:v})); setDirty(true); };
  const setTpl = (key, field, val) => {
    setCfg(p => ({ ...p, templates: { ...p.templates, [key]: { ...p.templates[key], [field]: val } } }));
    setDirty(true);
  };

  const applyPreset = (preset) => {
    setCfg(p => ({...p, smtpHost:preset.host, smtpPort:preset.port, smtpEncryption:preset.enc}));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveEmailConfig(pid, cfg);
      showToast('✅ Email configuration saved');
      setDirty(false);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { showToast('Enter a test email address'); return; }
    setTesting(true);
    try {
      await testEmailConfig(pid, { email: testEmail });
      showToast(`✅ Test email sent to ${testEmail}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setTesting(false); }
  };

  const tplMeta = TEMPLATES[tplKey];
  const tplCfg  = cfg?.templates?.[tplKey];

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner /></div>;

  const VARS_BY_TPL = {
    confirmation:    ['guestName','confirmationNumber','roomType','checkInDate','checkOutDate','checkInTime','checkOutTime','adults','children','mealPlan','ratePerNight','nights','totalAmount','currency'],
    checkinReminder: ['guestName','confirmationNumber','roomType','checkInDate','checkInTime','hotelAddress','directions'],
    checkout:        ['guestName','confirmationNumber','roomNumber','roomType','checkInDate','checkOutDate','nights','chargeLines','totalPaid','currency','paymentSummary','bookingUrl'],
    cancellation:    ['guestName','confirmationNumber','checkInDate','checkOutDate','roomType','cancellationDate','cancellationPolicy','refundAmount','currency','bookingUrl','phone'],
    payment:         ['guestName','confirmationNumber','roomNumber','roomType','checkInDate','checkOutDate','paymentDate','amount','method','reference','balance','currency'],
    staffWelcome:    ['staffName','username','tempPassword','role','systemUrl'],
  };

  return (
    <div className="fade-up">
      <style>{`
        .ec-toggle { position:relative; width:46px; height:26px; }
        .ec-toggle input { opacity:0; width:0; height:0; }
        .ec-slider { position:absolute; inset:0; background:#ccc; border-radius:99px; cursor:pointer; transition:.3s; }
        .ec-slider:before { content:''; position:absolute; height:20px; width:20px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:.3s; box-shadow:0 1px 4px rgba(0,0,0,.2); }
        input:checked + .ec-slider { background:${T.teal}; }
        input:checked + .ec-slider:before { transform:translateX(20px); }
        .ec-tpl-btn { padding:8px 12px; border-radius:9px; border:1.5px solid; cursor:pointer; transition:all .18s; font-family:inherit; font-size:11px; font-weight:600; text-align:left; display:flex; align-items:center; gap:8px; }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        background:`linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
        borderRadius:18, padding:'22px 28px', marginBottom:22,
        display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:14, boxShadow:SHADOW.lg,
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:5 }}>
            <span style={{ fontSize:22 }}>📧</span>
            <h1 style={{ fontFamily:FONTS.display, fontSize:24, fontWeight:700, color:'#fff', margin:0 }}>
              Email Configuration
            </h1>
          </div>
          <p style={{ fontSize:12, color:'rgba(255,255,255,.55)', margin:0 }}>
            SMTP · Sender Identity · {Object.keys(cfg.templates||{}).length} email templates
          </p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.1)', borderRadius:10, padding:'7px 14px' }}>
            <span style={{ fontSize:11, color:'rgba(255,255,255,.7)' }}>Custom SMTP</span>
            <label className="ec-toggle">
              <input type="checkbox" checked={cfg.smtpEnabled||false}
                onChange={e => set('smtpEnabled', e.target.checked)} />
              <span className="ec-slider" />
            </label>
          </div>
          <Btn variant="primary" disabled={saving || !dirty} onClick={handleSave}>
            {saving ? '⏳ Saving…' : '💾 Save'}
          </Btn>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'190px 1fr', gap:18, alignItems:'start' }}>

        {/* Sidebar */}
        <div style={{ position:'sticky', top:80 }}>
          <NavItem icon="📡" label="SMTP Server"   desc="Connection & auth" active={tab==='smtp'}      onClick={()=>setTab('smtp')} />
          <NavItem icon="✉️" label="Sender ID"     desc="From name & address" active={tab==='sender'}  onClick={()=>setTab('sender')} />
          <NavItem icon="📝" label="Templates"     desc="All email templates" active={tab==='templates'} onClick={()=>setTab('templates')} />

          {/* Status summary */}
          <div style={{ marginTop:16, background:T.white, borderRadius:12, border:`1px solid ${T.border}`, padding:'12px 14px', boxShadow:SHADOW.sm }}>
            <p style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.8, marginBottom:10 }}>Status</p>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background: cfg.smtpEnabled ? T.success : T.muted }} />
              <span style={{ fontSize:11, color: cfg.smtpEnabled ? T.success : T.muted, fontWeight:600 }}>
                {cfg.smtpEnabled ? 'Custom SMTP' : 'System Mailer'}
              </span>
            </div>
            {cfg.smtpEnabled && cfg.smtpHost && (
              <p style={{ fontSize:9, color:T.muted, fontFamily:'monospace', marginBottom:4 }}>{cfg.smtpHost}:{cfg.smtpPort}</p>
            )}
            {cfg.senderEmail && (
              <p style={{ fontSize:9, color:T.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                From: {cfg.senderEmail}
              </p>
            )}
          </div>
        </div>

        {/* Main content */}
        <div>

          {/* ── SMTP ──────────────────────────────────────────────────── */}
          {tab === 'smtp' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {/* Presets */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:4 }}>Quick Presets</p>
                <p style={{ fontSize:11, color:T.muted, marginBottom:14 }}>Click to auto-fill the server settings</p>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {SMTP_PRESETS.map(p => {
                    const active = cfg.smtpHost === p.host && p.host;
                    return (
                      <button key={p.label} onClick={() => applyPreset(p)} style={{
                        background: active ? `${T.teal}10` : T.surface,
                        border: `1.5px solid ${active ? T.teal : T.border}`,
                        borderRadius:10, padding:'10px 14px', cursor:'pointer',
                        fontFamily:FONTS.body, textAlign:'left',
                      }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontSize:13, fontWeight:700, color: active ? T.teal : T.navy }}>{p.label}</span>
                          {active && <span style={{ fontSize:9, color:T.teal, fontWeight:800 }}>SELECTED</span>}
                        </div>
                        {p.note && <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>{p.note}</p>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Credentials */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:16 }}>SMTP Credentials</p>
                <Field label="SMTP Host">
                  <Input value={cfg.smtpHost||''} onChange={v=>set('smtpHost',v)} placeholder="smtp.gmail.com" />
                </Field>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
                  <Field label="Port">
                    <Input type="number" value={cfg.smtpPort||587} onChange={v=>set('smtpPort',parseInt(v))} />
                  </Field>
                  <Field label="Encryption">
                    <Select value={cfg.smtpEncryption||'tls'} onChange={v=>set('smtpEncryption',v)}>
                      {['tls','ssl','none'].map(e=><option key={e} value={e}>{e.toUpperCase()}</option>)}
                    </Select>
                  </Field>
                </div>
                <Field label="SMTP Username">
                  <Input value={cfg.smtpUsername||''} onChange={v=>set('smtpUsername',v)} placeholder="you@domain.com" />
                </Field>
                <Field label="SMTP Password" hint={cfg.smtpPassword==='••••••••' ? 'Saved — enter new to change' : ''}>
                  <Input type="password" value={cfg.smtpPassword||''} onChange={v=>set('smtpPassword',v)} placeholder="App password or SMTP password" />
                </Field>

                <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:14, marginTop:8 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.7, marginBottom:10 }}>
                    Test Configuration
                  </p>
                  <div style={{ display:'flex', gap:10 }}>
                    <Input value={testEmail} onChange={setTestEmail} placeholder="Send test to…" style={{ flex:1 }} />
                    <Btn variant="secondary" disabled={testing} onClick={handleTest}>
                      {testing ? '⏳' : '📨 Send'}
                    </Btn>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── SENDER IDENTITY ───────────────────────────────────────── */}
          {tab === 'sender' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:4 }}>Sender Identity</p>
                <p style={{ fontSize:12, color:T.muted, marginBottom:18 }}>
                  How your hotel appears in guests' email inboxes.
                </p>
                <Field label="Display Name" hint='What guests see in "From:"'>
                  <Input value={cfg.senderName||''} onChange={v=>set('senderName',v)}
                    placeholder={`${property?.name || 'Hotel'} Reservations`} />
                </Field>
                <Field label="Sender Email" hint="Must match SMTP username or authorised alias">
                  <Input type="email" value={cfg.senderEmail||''} onChange={v=>set('senderEmail',v)}
                    placeholder="reservations@yourhotel.com" />
                </Field>
                <Field label="Reply-To" hint="Guest replies go here (optional)">
                  <Input type="email" value={cfg.replyTo||''} onChange={v=>set('replyTo',v)}
                    placeholder="info@yourhotel.com" />
                </Field>
              </div>

              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:14 }}>Preview</p>
                {/* Inbox preview */}
                <div style={{ background:'#f8fafc', borderRadius:12, border:`1px solid ${T.border}`, overflow:'hidden', marginBottom:14 }}>
                  <div style={{ background:'#fff', padding:'10px 14px', borderBottom:`1px solid ${T.border}` }}>
                    <p style={{ fontSize:10, color:T.muted, fontWeight:600, letterSpacing:.6, textTransform:'uppercase' }}>Inbox preview</p>
                  </div>
                  <div style={{ padding:'12px 14px' }}>
                    <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:`${T.teal}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>🏨</div>
                      <div>
                        <p style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>
                          {cfg.senderName || property?.name || 'Hotel Name'}
                        </p>
                        <p style={{ fontSize:11, color:T.muted }}>
                          {cfg.senderEmail || 'noreply@yourhotel.com'}
                        </p>
                        <p style={{ fontSize:12, color:'#374151', marginTop:4, fontWeight:600 }}>Your booking is confirmed — {property?.name}</p>
                        <p style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>Dear Guest, Thank you for choosing {property?.name}. We are delighted…</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ background:T.tealPale, borderRadius:10, padding:'12px 14px', fontSize:12, color:T.teal, lineHeight:1.6 }}>
                  <strong>From:</strong> {cfg.senderName || property?.name || 'Hotel'} &lt;{cfg.senderEmail || 'noreply@hotelmis.com'}&gt;<br />
                  {cfg.replyTo && <><strong>Reply-To:</strong> {cfg.replyTo}</>}
                </div>
              </div>
            </div>
          )}

          {/* ── TEMPLATES ──────────────────────────────────────────────── */}
          {tab === 'templates' && (
            <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:14 }}>

              {/* Template list */}
              <div style={{ background:T.white, borderRadius:14, border:`1px solid ${T.border}`, padding:'14px', boxShadow:SHADOW.sm }}>
                <p style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.8, marginBottom:10, paddingLeft:4 }}>Templates</p>
                {Object.entries(TEMPLATES).map(([key, meta]) => {
                  const enabled = cfg.templates?.[key]?.enabled !== false;
                  return (
                    <button key={key} className="ec-tpl-btn"
                      onClick={() => setTplKey(key)}
                      style={{
                        width:'100%', marginBottom:3,
                        borderColor: tplKey===key ? T.teal : T.border,
                        background: tplKey===key ? `${T.teal}10` : 'transparent',
                        color: tplKey===key ? T.teal : T.navy,
                      }}>
                      <span style={{ fontSize:16 }}>{meta.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{meta.label}</span>
                        <span style={{ fontSize:9, color: enabled ? T.success : T.muted }}>
                          {enabled ? '● Active' : '○ Off'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Template editor */}
              {tplCfg && tplMeta && (
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:22 }}>{tplMeta.icon}</span>
                      <div>
                        <p style={{ fontSize:14, fontWeight:700, color:T.navy }}>{tplMeta.label}</p>
                        <p style={{ fontSize:11, color:T.muted }}>Sent automatically when triggered</p>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                      <span style={{ fontSize:11, color:T.muted }}>
                        {tplCfg.enabled !== false ? 'Enabled' : 'Disabled'}
                      </span>
                      <label className="ec-toggle">
                        <input type="checkbox"
                          checked={tplCfg.enabled !== false}
                          onChange={e => setTpl(tplKey, 'enabled', e.target.checked)} />
                        <span className="ec-slider" />
                      </label>
                      <Btn size="sm" variant="ghost" onClick={() => setPreview(!preview)}>
                        {preview ? '✏️ Edit' : '👁 Preview'}
                      </Btn>
                      <Btn size="sm" variant="ghost"
                        onClick={() => { setTpl(tplKey,'subject',tplMeta.subject); setTpl(tplKey,'body',tplMeta.body); }}>
                        ↺ Reset
                      </Btn>
                    </div>
                  </div>

                  {!preview ? (
                    <>
                      <Field label="Subject Line">
                        <Input value={tplCfg.subject||''} onChange={v=>setTpl(tplKey,'subject',v)} />
                      </Field>
                      <Field label="Email Body" hint="Plain text — HTML tags supported">
                        <textarea
                          value={tplCfg.body||''}
                          onChange={e=>setTpl(tplKey,'body',e.target.value)}
                          rows={18}
                          style={{
                            width:'100%', border:`1.5px solid ${T.border}`, borderRadius:10,
                            padding:'10px 13px', fontSize:12, fontFamily:'monospace',
                            color:T.navy, outline:'none', resize:'vertical',
                            lineHeight:1.6, boxSizing:'border-box',
                          }} />
                      </Field>

                      {/* Variable chips */}
                      <div style={{ marginTop:10 }}>
                        <p style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.7, textTransform:'uppercase', marginBottom:8 }}>
                          Available Variables — click to copy
                        </p>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                          {(VARS_BY_TPL[tplKey] || []).map(v => <VarChip key={v} name={v} />)}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Preview pane */
                    <div style={{ border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden' }}>
                      {/* Simulated email client header */}
                      <div style={{ background:'#f1f5f9', padding:'12px 18px', borderBottom:`1px solid ${T.border}` }}>
                        <p style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{tplCfg.subject||'(no subject)'}</p>
                        <p style={{ fontSize:11, color:'#64748b', marginTop:3 }}>
                          From: {cfg.senderName||property?.name||'Hotel'} &lt;{cfg.senderEmail||'noreply@hotel.com'}&gt;
                        </p>
                        <p style={{ fontSize:11, color:'#64748b' }}>To: guest@email.com</p>
                      </div>
                      <div style={{ padding:'20px 22px', background:'#fff' }}>
                        <pre style={{
                          fontSize:12, color:'#1e293b', lineHeight:1.7, fontFamily:'system-ui,sans-serif',
                          whiteSpace:'pre-wrap', wordBreak:'break-word', margin:0,
                        }}>{tplCfg.body||'(empty)'}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      <Toast message={toast} />
    </div>
  );
}
